import { useState } from "react";
import { ArrowDown, ArrowUp, Eye, LayoutTemplate, PanelsTopLeft, Plus, Settings2, Trash2 } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { PORTAL_BLOCK_TYPES, SCREEN_BLOCK_TYPES, SCREEN_ICONS, ScreenRenderer, type ScreenBlock, type ScreenBlockType, type ScreenDefinitionView } from "@ncp/screens";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Field, InlineAlert, Input, NativeSelect, Switch, Textarea } from "@ncp/ui";
import { DefinitionEditorPage, type EditorContext } from "../features/generic/DefinitionEditor";
import { useDefinition } from "../features/generic/api";
import type { RecordTypeBody } from "../features/records/api";
import { fieldLabel } from "../features/records/RecordForm";
import { SCREEN_KINDS, blankBlock, useScreenOptions, type ScreenBody, type ScreenOptions, type ScreenRef } from "../features/screens/api";

type Ctx = EditorContext<ScreenBody>;
type T = (k: string, o?: Record<string, unknown>) => string;

const REF: Partial<Record<ScreenBlockType, "recordType" | "report" | "service">> = {
  recordList: "recordType", recordForm: "recordType", recordDetail: "recordType", report: "report", kpi: "report", serviceStart: "service", caseList: "service",
};

function lint(b: ScreenBody, t: T) {
  const errors: string[] = []; const warnings: string[] = [];
  if (b.blocks.length === 0) errors.push(t("admin:screens.lint.noBlocks"));
  b.blocks.forEach((block, i) => {
    const n = i + 1;
    if (b.surface === "portal" && !PORTAL_BLOCK_TYPES.includes(block.type)) errors.push(t("admin:screens.lint.portalBlock", { n, block: t(`admin:screens.blocks.${block.type}.name`) }));
    const ref = REF[block.type];
    if (ref && !(block as Record<string, unknown>)[ref]) errors.push(t("admin:screens.lint.missingRef", { n, block: t(`admin:screens.blocks.${block.type}.name`) }));
    if ((block.type === "heading" || block.type === "text") && !block.text.trim()) warnings.push(t("admin:screens.lint.emptyText", { n }));
  });
  if (b.surface === "backoffice" && b.blocks.some((x) => x.type === "recordDetail") && !b.blocks.some((x) => x.type === "recordList")) warnings.push(t("admin:screens.lint.detailWithoutList"));
  if (b.surface === "backoffice" && b.blocks.some((x) => x.type === "caseDetail") && !b.blocks.some((x) => x.type === "caseList")) warnings.push(t("admin:screens.lint.caseDetailWithoutList"));
  return { errors, warnings };
}

/** Screen Builder: the blocks of a screen (lists, forms, details, reports, service intake and case views), who sees it and where it sits in the menu, with a live preview. */
export function ScreenDesignerPage() {
  const { t } = useTranslation();
  return (
    <DefinitionEditorPage<ScreenBody>
      resource="screens"
      basePath="/screens"
      navLabel={t("admin:nav.screens")}
      icon={PanelsTopLeft}
      writePermission="platform.screens.write"
      docsAnchor="screens"
      help={<><p>{t("admin:screens.help.what")}</p><p>{t("admin:screens.help.portal")}</p><p>{t("admin:screens.help.release")}</p></>}
      tabs={[{ key: "design", label: t("admin:screens.tabs.design"), icon: LayoutTemplate }, { key: "settings", label: t("admin:screens.tabs.settings"), icon: Settings2 }]}
      lint={(b) => lint(b, t as T)}
      summary={(b) => t("admin:screens.summary", { surface: t(`admin:screens.surfaces.${b.surface}`), kind: t(`admin:screens.kinds.${b.kind}`), count: b.blocks.length })}
      badges={(b) => <Badge variant={b.surface === "portal" ? "gold" : "outline"} size="sm">{t(`admin:screens.surfaces.${b.surface}`)}</Badge>}
      render={(tab, ctx) => (tab === "settings" ? <SettingsTab ctx={ctx} /> : <DesignTab ctx={ctx} />)}
    />
  );
}

function DesignTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { body, patch, readOnly, definition } = ctx;
  const options = useScreenOptions();
  const allowed = body.surface === "portal" ? PORTAL_BLOCK_TYPES : SCREEN_BLOCK_TYPES;
  const [adding, setAdding] = useState<ScreenBlockType>(allowed[0]!);
  const setBlock = (i: number, next: ScreenBlock) => patch((b) => ({ ...b, blocks: b.blocks.map((x, j) => (j === i ? next : x)) }));
  const move = (i: number, d: -1 | 1) => patch((b) => { const blocks = [...b.blocks]; const [x] = blocks.splice(i, 1); blocks.splice(i + d, 0, x!); return { ...b, blocks }; });
  const view: ScreenDefinitionView = {
    key: definition.key, version: definition.version, name: body.name, nameAr: body.nameAr, surface: body.surface, kind: body.kind,
    recordTypeKey: body.recordTypeKey, serviceKey: body.serviceKey, blocks: body.blocks, showInMenu: body.showInMenu, menuIcon: body.menuIcon, menuOrder: body.menuOrder,
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
      <div className="space-y-3" data-testid="screen-blocks">
        {body.blocks.map((block, i) => (
          <Card key={i} data-testid={`screen-block-${i}`}>
            <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 py-3">
              <div className="min-w-0">
                <CardTitle className="text-sm">{i + 1}. {t(`admin:screens.blocks.${block.type}.name`)}</CardTitle>
                <CardDescription className="text-xs">{t(`admin:screens.blocks.${block.type}.hint`)}</CardDescription>
              </div>
              {!readOnly ? (
                <div className="flex shrink-0 gap-1">
                  <Button size="icon" variant="ghost" aria-label={t("admin:screens.moveUp")} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp /></Button>
                  <Button size="icon" variant="ghost" aria-label={t("admin:screens.moveDown")} disabled={i === body.blocks.length - 1} onClick={() => move(i, 1)}><ArrowDown /></Button>
                  <Button size="icon" variant="ghost" aria-label={t("admin:common.remove")} onClick={() => patch((b) => ({ ...b, blocks: b.blocks.filter((_, j) => j !== i) }))}><Trash2 /></Button>
                </div>
              ) : null}
            </CardHeader>
            <CardContent className="pt-0">
              <BlockEditor block={block} onChange={(next) => setBlock(i, next)} options={options.data} readOnly={readOnly} portal={body.surface === "portal"} />
            </CardContent>
          </Card>
        ))}
        {body.blocks.length === 0 ? <InlineAlert tone="info">{t("admin:screens.noBlocks")}</InlineAlert> : null}
        {!readOnly ? (
          <div className="flex gap-2">
            <NativeSelect data-testid="screen-add-type" value={adding} onChange={(e) => setAdding(e.target.value as ScreenBlockType)} className="max-w-xs">
              {allowed.map((b) => <option key={b} value={b}>{t(`admin:screens.blocks.${b}.name`)}</option>)}
            </NativeSelect>
            <Button data-testid="screen-add-block" variant="outline" disabled={body.blocks.length >= 24} onClick={() => patch((b) => ({ ...b, blocks: [...b.blocks, blankBlock(adding, b, options.data)] }))}><Plus /> {t("admin:screens.addBlock")}</Button>
          </div>
        ) : null}
      </div>

      <Card className="xl:sticky xl:top-40 xl:self-start">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Eye className="size-4" /> {t("admin:screens.preview.title")}</CardTitle>
          <CardDescription>{body.surface === "portal" ? t("admin:screens.preview.portal") : t("admin:screens.preview.backoffice")}</CardDescription>
        </CardHeader>
        <CardContent className="max-h-[75vh] overflow-y-auto rounded-b-2xl bg-sand-50/60 p-4" data-testid="screen-preview">
          <ScreenRenderer surface="backoffice" locale={locale === "ar" ? "ar" : "en"} definition={view} preview />
        </CardContent>
      </Card>
    </div>
  );
}

function RefSelect({ value, refs, onChange, readOnly, label, testId }: { value: string; refs?: ScreenRef[]; onChange: (v: string) => void; readOnly: boolean; label: string; testId?: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const current = refs?.find((r) => r.key === value);
  return (
    <Field label={label} hint={current && !current.published ? t("admin:screens.notPublished") : undefined}>
      <NativeSelect data-testid={testId} value={value} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {refs?.map((r) => <option key={r.key} value={r.key}>{locale === "ar" && r.nameAr ? r.nameAr : r.name}{r.published ? "" : ` (${t("admin:screens.draft")})`}</option>)}
      </NativeSelect>
    </Field>
  );
}

function Bilingual({ label, en, ar, onChange, readOnly, long }: { label: string; en?: string | null; ar?: string | null; onChange: (en: string | null, ar: string | null) => void; readOnly: boolean; long?: boolean }) {
  const { t } = useTranslation();
  const C = long ? Textarea : Input;
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <Field label={label}><C value={en ?? ""} disabled={readOnly} onChange={(e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => onChange(e.target.value, ar ?? null)} /></Field>
      <Field label={`${label} · ${t("admin:screens.arabic")}`}><C dir="rtl" value={ar ?? ""} disabled={readOnly} onChange={(e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => onChange(en ?? "", e.target.value || null)} /></Field>
    </div>
  );
}

function FieldPicker({ recordType, value, onChange, readOnly, label, hint }: { recordType: string; value?: string[]; onChange: (v: string[]) => void; readOnly: boolean; label: string; hint: string }) {
  const { locale } = useLocale();
  const type = useDefinition<RecordTypeBody>("record-types", recordType || undefined);
  const fields = type.data?.fields ?? [];
  if (!recordType || fields.length === 0) return null;
  const chosen = value ?? [];
  return (
    <Field label={label} hint={hint}>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {fields.map((f) => (
          <label key={f.key} className="flex items-center gap-2 text-sm">
            <Checkbox checked={chosen.includes(f.key)} disabled={readOnly} onCheckedChange={(c) => onChange(c === true ? [...chosen, f.key] : chosen.filter((k) => k !== f.key))} /> {fieldLabel(f, locale)}
          </label>
        ))}
      </div>
    </Field>
  );
}

function BlockEditor({ block, onChange, options, readOnly, portal }: { block: ScreenBlock; onChange: (b: ScreenBlock) => void; options?: ScreenOptions; readOnly: boolean; portal: boolean }) {
  const { t } = useTranslation();
  const services = portal ? options?.services.filter((s) => s.portal) : options?.services;
  const toggle = (label: string, checked: boolean | undefined, set: (v: boolean) => void) => (
    <label className="flex items-center gap-2 text-sm"><Switch checked={checked !== false} disabled={readOnly} onCheckedChange={set} /> {label}</label>
  );
  switch (block.type) {
    case "heading": return (
      <div className="space-y-2">
        <Bilingual label={t("admin:screens.props.heading")} en={block.text} ar={block.textAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, text: en ?? "", textAr: ar })} />
        <Bilingual label={t("admin:screens.props.subtitle")} en={block.body} ar={block.bodyAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, body: en || null, bodyAr: ar })} />
      </div>
    );
    case "text": return <Bilingual long label={t("admin:screens.props.text")} en={block.text} ar={block.textAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, text: en ?? "", textAr: ar })} />;
    case "recordList": return (
      <div className="space-y-2">
        <div className="grid gap-2 sm:grid-cols-2">
          <RefSelect testId="block-record-type" label={t("admin:screens.fields.recordType")} value={block.recordType} refs={options?.recordTypes} readOnly={readOnly} onChange={(v) => onChange({ ...block, recordType: v, columns: [] })} />
          <Field label={t("admin:screens.props.pageSize")}><Input type="number" min={5} max={100} value={block.pageSize ?? 20} disabled={readOnly} onChange={(e) => onChange({ ...block, pageSize: Number(e.target.value) || 20 })} /></Field>
        </div>
        <Bilingual label={t("admin:screens.props.title")} en={block.title} ar={block.titleAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, title: en || null, titleAr: ar })} />
        <FieldPicker recordType={block.recordType} value={block.columns} readOnly={readOnly} label={t("admin:screens.props.columns")} hint={t("admin:screens.props.columnsHint")} onChange={(columns) => onChange({ ...block, columns })} />
        {toggle(t("admin:screens.props.allowCreate"), block.allowCreate, (v) => onChange({ ...block, allowCreate: v }))}
      </div>
    );
    case "recordForm": return (
      <div className="space-y-2">
        <RefSelect testId="block-record-type" label={t("admin:screens.fields.recordType")} value={block.recordType} refs={options?.recordTypes} readOnly={readOnly} onChange={(v) => onChange({ ...block, recordType: v, fields: [] })} />
        <Bilingual label={t("admin:screens.props.title")} en={block.title} ar={block.titleAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, title: en || null, titleAr: ar })} />
        <FieldPicker recordType={block.recordType} value={block.fields} readOnly={readOnly} label={t("admin:screens.props.fields")} hint={t("admin:screens.props.fieldsHint")} onChange={(fields) => onChange({ ...block, fields })} />
        <Bilingual label={t("admin:screens.props.submitLabel")} en={block.submitLabel} ar={block.submitLabelAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, submitLabel: en || null, submitLabelAr: ar })} />
      </div>
    );
    case "recordDetail": return (
      <div className="space-y-2">
        <RefSelect testId="block-record-type" label={t("admin:screens.fields.recordType")} value={block.recordType} refs={options?.recordTypes} readOnly={readOnly} onChange={(v) => onChange({ ...block, recordType: v, fields: [] })} />
        <FieldPicker recordType={block.recordType} value={block.fields} readOnly={readOnly} label={t("admin:screens.props.fields")} hint={t("admin:screens.props.fieldsHint")} onChange={(fields) => onChange({ ...block, fields })} />
        {toggle(t("admin:screens.props.showLinks"), block.showLinks, (v) => onChange({ ...block, showLinks: v }))}
      </div>
    );
    case "report": return (
      <div className="space-y-2">
        <RefSelect testId="block-report" label={t("admin:screens.props.report")} value={block.report} refs={options?.reports} readOnly={readOnly} onChange={(v) => onChange({ ...block, report: v })} />
        <Bilingual label={t("admin:screens.props.title")} en={block.title} ar={block.titleAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, title: en || null, titleAr: ar })} />
      </div>
    );
    case "kpi": return (
      <div className="space-y-2">
        <div className="grid gap-2 sm:grid-cols-2">
          <RefSelect testId="block-report" label={t("admin:screens.props.report")} value={block.report} refs={options?.reports} readOnly={readOnly} onChange={(v) => onChange({ ...block, report: v })} />
          <Field label={t("admin:screens.props.column")} hint={t("admin:screens.props.columnHint")}><Input value={block.column ?? ""} dir="ltr" disabled={readOnly} onChange={(e) => onChange({ ...block, column: e.target.value || null })} /></Field>
        </div>
        <Bilingual label={t("admin:screens.props.label")} en={block.label} ar={block.labelAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, label: en || null, labelAr: ar })} />
      </div>
    );
    case "serviceStart": return (
      <div className="space-y-2">
        <RefSelect testId="block-service" label={t("admin:screens.fields.service")} value={block.service} refs={services} readOnly={readOnly} onChange={(v) => onChange({ ...block, service: v })} />
        <Bilingual label={t("admin:screens.props.title")} en={block.label} ar={block.labelAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, label: en || null, labelAr: ar })} />
        <Bilingual long label={t("admin:screens.props.intro")} en={block.body} ar={block.bodyAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, body: en || null, bodyAr: ar })} />
      </div>
    );
    case "caseList": return (
      <div className="space-y-2">
        <RefSelect testId="block-service" label={t("admin:screens.fields.service")} value={block.service} refs={services} readOnly={readOnly} onChange={(v) => onChange({ ...block, service: v })} />
        <Bilingual label={t("admin:screens.props.title")} en={block.title} ar={block.titleAr} readOnly={readOnly} onChange={(en, ar) => onChange({ ...block, title: en || null, titleAr: ar })} />
      </div>
    );
    case "caseDetail": return (
      <div className="grid gap-2 sm:grid-cols-2">
        {toggle(t("admin:screens.props.showActions"), block.showActions, (v) => onChange({ ...block, showActions: v }))}
        {toggle(t("admin:screens.props.showTasks"), block.showTasks, (v) => onChange({ ...block, showTasks: v }))}
        {toggle(t("admin:screens.props.showTimeline"), block.showTimeline, (v) => onChange({ ...block, showTimeline: v }))}
        {toggle(t("admin:screens.props.showDocuments"), block.showDocuments, (v) => onChange({ ...block, showDocuments: v }))}
      </div>
    );
  }
}

function SettingsTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { body, patch, readOnly, definition } = ctx;
  const options = useScreenOptions();
  const Icon = SCREEN_ICONS[body.menuIcon];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>{t("admin:screens.settings.whereTitle")}</CardTitle><CardDescription>{t("admin:screens.settings.whereIntro", { path: `/s/${definition.key}` })}</CardDescription></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:screens.fields.surface")}>
            <NativeSelect data-testid="screen-surface" value={body.surface} disabled={readOnly} onChange={(e) => patch({ surface: e.target.value as ScreenBody["surface"], roleKeys: e.target.value === "portal" ? [] : body.roleKeys })}>
              <option value="backoffice">{t("admin:screens.surfaces.backoffice")}</option>
              <option value="portal">{t("admin:screens.surfaces.portal")}</option>
            </NativeSelect>
          </Field>
          <Field label={t("admin:screens.fields.kind")}>
            <NativeSelect data-testid="screen-kind" value={body.kind} disabled={readOnly} onChange={(e) => patch({ kind: e.target.value as ScreenBody["kind"] })}>
              {SCREEN_KINDS.map((k) => <option key={k} value={k}>{t(`admin:screens.kinds.${k}`)}</option>)}
            </NativeSelect>
          </Field>
          <RefSelect label={t("admin:screens.fields.recordType")} value={body.recordTypeKey ?? ""} refs={options.data?.recordTypes} readOnly={readOnly} onChange={(v) => patch({ recordTypeKey: v || null })} />
          <RefSelect label={t("admin:screens.fields.service")} value={body.serviceKey ?? ""} refs={options.data?.services} readOnly={readOnly} onChange={(v) => patch({ serviceKey: v || null })} />
          {body.surface === "portal" ? <InlineAlert tone="info" className="sm:col-span-2">{t("admin:screens.settings.portalNote")}</InlineAlert> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{t("admin:screens.settings.menuTitle")}</CardTitle><CardDescription>{t("admin:screens.settings.menuIntro")}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={body.showInMenu} disabled={readOnly} onCheckedChange={(c) => patch({ showInMenu: c })} /> {t("admin:screens.settings.showInMenu")}</label>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin:screens.settings.icon")}>
              <div className="flex items-center gap-2">
                {Icon ? <Icon className="size-5 text-aegold-700" /> : null}
                <NativeSelect data-testid="screen-icon" value={body.menuIcon} disabled={readOnly} onChange={(e) => patch({ menuIcon: e.target.value })}>
                  {Object.keys(SCREEN_ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
                </NativeSelect>
              </div>
            </Field>
            <Field label={t("admin:screens.settings.order")} hint={t("admin:screens.settings.orderHint")}><Input type="number" value={body.menuOrder} disabled={readOnly} onChange={(e) => patch({ menuOrder: Number(e.target.value) || 0 })} /></Field>
          </div>
        </CardContent>
      </Card>

      {body.surface === "backoffice" ? (
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>{t("admin:screens.settings.accessTitle")}</CardTitle><CardDescription>{t("admin:screens.settings.accessIntro")}</CardDescription></CardHeader>
          <CardContent>
            <div className="grid gap-1.5 sm:grid-cols-3">
              {options.data?.roles.map((r) => (
                <label key={r.key} className="flex items-center gap-2 text-sm">
                  <Checkbox data-testid={`screen-role-${r.key}`} checked={body.roleKeys.includes(r.key)} disabled={readOnly}
                    onCheckedChange={(c) => patch((b) => ({ ...b, roleKeys: c === true ? [...b.roleKeys, r.key] : b.roleKeys.filter((k) => k !== r.key) }))} />
                  {locale === "ar" && r.nameAr ? r.nameAr : r.name}
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{body.roleKeys.length ? t("admin:screens.settings.accessSome", { count: body.roleKeys.length }) : t("admin:screens.settings.accessAll")}</p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
